import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import type { Sys } from 'src/types/contentful-sdk-compat';
import Head from 'next/head';
import React, { useCallback, useId, useMemo } from 'react';
import Icon from 'threads5/Icon';
import Touchable from 'threads5/Touchable';

import { Box, Column, Stack } from 'src/components-v2/Layout';
import theme from 'src/components-v2/theme';
import {
  Answer,
  Divider,
  FaqRowContainer,
  Question,
  Strip,
  StyledStack,
} from './Faq.styled';
import type { FaqItem, FaqProps } from './Faq.types';

interface FaqRowProps {
  question: string;
  answer: React.ReactNode;
  expanded: number | null;
  index: number;
  onClick: (index: number) => void;
  sys?: Sys;
  styles?: Record<string, unknown>;
}

const FaqRow: React.FC<FaqRowProps> = ({
  question,
  answer,
  expanded,
  index,
  onClick,
  sys,
  styles = {},
}) => {
  const accordionId = `accordion-${useId()}`;
  const isExpanded = expanded === index;
  const handleClick = useCallback(() => {
    if (isExpanded) {
      onClick(null);
    } else {
      onClick(index);
    }
  }, [index, onClick, isExpanded]);
  const inspectorProps = useContentfulInspectorMode({
    entryId: sys?.id,
  });

  return (
    <FaqRowContainer isExpanded={isExpanded} sx={styles}>
      <Box
        className='faq-row-background'
        sx={{
          background:
            'linear-gradient(48deg, #F3EBFE 0%, #E5EFFE 50%, #CDF5FD 100%)',
          opacity: 0,
          transition: 'opacity 0.3s ease',
          position: 'absolute',
          width: '100%',
          height: '100%',
        }}
      />
      <Box
        sx={{
          zIndex: 1,
          position: 'relative',
        }}
      >
        <StyledStack isExpanded={isExpanded}>
          <Touchable
            id={`${accordionId}-control`}
            type='button'
            onClick={handleClick}
            ariaControls={accordionId}
            ariaExpanded={isExpanded}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            sx={{
              '&:focus-visible': {
                outlineWidth: '2px',
                outlineColor: theme.palette['web3/component/button/outline'],
                outlineStyle: 'solid',
                outlineOffset: '3px',
              },
            }}
          >
            <Stack
              direction='row'
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Question {...inspectorProps({ fieldId: 'question' })}>
                  {question}
                </Question>
              </Box>
              {
                <Icon
                  className='expand-icon'
                  name={isExpanded ? 'Subtract' : 'Add'}
                  size='20'
                />
              }
            </Stack>
          </Touchable>
          <Box
            id={accordionId}
            aria-labelledby={`${accordionId}-control`}
            mt={isExpanded ? 1.5 : 0}
            sx={{
              display: 'grid',
              maxHeight: isExpanded ? '1000px' : '0px',
              opacity: isExpanded ? 1 : 0,
              overflow: 'hidden',
              transition:
                'padding 0.3s ease, background 0.3s ease, margin 0.3s ease, opacity 0.3s ease, max-height 0.1s ease',
            }}
          >
            <Answer as={typeof answer === 'string' ? 'p' : 'div'}>
              {answer}
            </Answer>
          </Box>
        </StyledStack>
        <Strip
          sx={{
            opacity: isExpanded ? 1 : 0,
            height: isExpanded ? '3px' : '0px',
            transition: 'height 0.3s ease, opacity 0.3s ease',
          }}
        />

        <Divider isVisible={!(expanded === index + 1 || expanded === index)} />
      </Box>
    </FaqRowContainer>
  );
};

const makeStructuredData = (faqItems: FaqItem[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((faq) => {
      return {
        '@type': 'Question',
        name: faq.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq.plainTextAnswer,
        },
      };
    }),
  };
};

const FaqTable = ({ faqItems, includeFaqSchema = false }: FaqProps) => {
  const [expanded, setExpanded] = React.useState(0);
  const handleClick = useCallback((index: number) => {
    setExpanded(index);
  }, []);

  const structuredData = useMemo(() => {
    return makeStructuredData(faqItems);
  }, [faqItems]);

  return (
    <Column xs={24} md={12} lg={11} lgOffset={1}>
      {faqItems.map((faq, index) => {
        return (
          <FaqRow
            key={index}
            sys={faq?.sys}
            question={faq.question}
            answer={faq.answer}
            index={index}
            onClick={handleClick}
            expanded={expanded}
            styles={faq?.styles}
          />
        );
      })}
      {includeFaqSchema && (
        <Head>
          <script
            key='faq-schema'
            type='application/ld+json'
            dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
          />
        </Head>
      )}
    </Column>
  );
};

export default FaqTable;
